"use client";
/**
 * Tiny client-side event bus, bridged across browser tabs.
 *
 * Used to coordinate cross-route side effects in the SPA — e.g. when
 * the user changes a reading's universe at /settings/breadth and the
 * Daily Brief needs to follow it immediately.
 * No external dependency, no global state library; just a typed
 * Set<callback> + an emit helper.
 *
 * Why an event bus rather than Zustand/Context: the data flow is
 * fire-and-forget ("a setting just changed") and the listeners live
 * in unrelated subtrees. A subscribe/emit pattern fits that shape
 * better than a shared store, and stays out of the way of components
 * that don't care.
 *
 * Cross-tab: a `BroadcastChannel` mirrors every emit to every other
 * tab on the same origin. The classic use-case — uploading a new
 * S&P 1500 list in /settings/indices while the Daily Brief sits in
 * a separate tab — now triggers the same instant refetch in BOTH
 * tabs instead of leaving the Daily Brief stale until its next 60s
 * poll. Falls back to in-tab-only when BroadcastChannel isn't
 * available (very old browsers, server-side hydration).
 */
import { useEffect } from "react";

type EventMap = {
  /** Settings › Breadth saved a reading's universe. Pages that open on
   *  it, and readings that follow it, ask for it again. */
  "breadth-readings-changed": void;
  /** An index_membership list was uploaded or cleared via Settings →
   *  Indices. Panes that filter by an uploaded universe (Breadth
   *  Dashboard, Advance/Decline, Buckets, Market Performance) listen
   *  for this so the user doesn't have to wait for the 60s polling
   *  tick. The payload is the slug that just changed, so listeners
   *  can decide whether their current selection is affected. */
  "index-uploaded": { slug: string };
  /** Settings → Maintenance added/removed an ETF Theme. The Scanner
   *  Themes pane refetches the catalog. */
  "etf-themes-changed": void;
  /** A chart's drawings for a symbol were saved (added / moved / removed /
   *  cleared). Other charts showing the SAME symbol listen for this and
   *  reload from storage so two charts on one symbol stay in sync — a
   *  delete on one no longer gets clobbered by another's stale in-memory
   *  copy. `sourceId` is the originating chart so it ignores its own echo. */
  "drawings-changed": {
    symbol: string;
    sourceId: string;
    /** Page/layout id when scope is `layout`. */
    layoutId?: string;
    /** Emitter's sync mode — receivers filter on this. */
    scope?: "layout" | "global";
  };
  /** Overlay drawings (anchored VWAP, volume profiles, patterns, markers)
   *  were saved. The TradingView bridge and other Advanced panes on this
   *  symbol reload from `overlayStore`. */
  "overlays-changed": {
    symbol: string;
    sourceId: string;
  };
};

type Listener<K extends keyof EventMap> = (payload: EventMap[K]) => void;

const subscribers = new Map<keyof EventMap, Set<Listener<keyof EventMap>>>();

/** BroadcastChannel used to mirror emits to other tabs. Created lazily
 *  on the first emit/subscribe so SSR (where `window` is undefined)
 *  never trips on it. The channel is bidirectional — every tab posts
 *  AND listens on the same channel, so we tag outbound messages with
 *  a per-tab token and ignore our own echoes. */
const TAB_ID =
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `tab-${Math.random().toString(36).slice(2)}-${Date.now()}`;

let channel: BroadcastChannel | null = null;
function getChannel(): BroadcastChannel | null {
  if (channel) return channel;
  if (typeof window === "undefined") return null;
  if (typeof BroadcastChannel === "undefined") return null;
  channel = new BroadcastChannel("mpp:client-events");
  channel.addEventListener("message", (e) => {
    const msg = e.data as
      | { tabId: string; event: keyof EventMap; payload: unknown }
      | undefined;
    if (!msg || typeof msg !== "object") return;
    // Ignore the loopback echo we just posted ourselves — only
    // dispatch when the message came from a different tab.
    if (msg.tabId === TAB_ID) return;
    dispatchLocal(msg.event, msg.payload as EventMap[keyof EventMap]);
  });
  return channel;
}

/** Fire local subscribers without re-broadcasting. Used by both the
 *  user-facing `emit` and the cross-tab message handler — splitting
 *  it out keeps the loop-guard logic in one place. */
function dispatchLocal<K extends keyof EventMap>(
  event: K,
  payload: EventMap[K],
): void {
  const set = subscribers.get(event);
  if (!set) return;
  for (const fn of set) fn(payload);
}

export function emit<K extends keyof EventMap>(
  event: K,
  payload?: EventMap[K],
): void {
  dispatchLocal(event, payload as EventMap[K]);
  const ch = getChannel();
  if (ch) {
    try {
      ch.postMessage({ tabId: TAB_ID, event, payload });
    } catch {
      // Structured-clone failed (payload contained a function, a DOM
      // node, etc.) — every event in EventMap is plain JSON today, so
      // this should never fire. Stay silent rather than tripping up
      // the local dispatch we already finished.
    }
  }
}

/** Imperatively subscribe. Returns an unsubscribe function. */
export function subscribe<K extends keyof EventMap>(
  event: K,
  fn: Listener<K>,
): () => void {
  // Lazy-init the cross-tab channel here so a tab that ONLY listens
  // (e.g. the Daily Brief, which never emits index-uploaded itself)
  // still receives broadcasts from the tab that does emit.
  getChannel();
  let set = subscribers.get(event);
  if (!set) {
    set = new Set();
    subscribers.set(event, set);
  }
  set.add(fn as Listener<keyof EventMap>);
  return () => {
    set?.delete(fn as Listener<keyof EventMap>);
  };
}

/** React-friendly wrapper — subscribes on mount, cleans up on unmount. */
export function useClientEvent<K extends keyof EventMap>(
  event: K,
  fn: Listener<K>,
): void {
  useEffect(() => subscribe(event, fn), [event, fn]);
}
